import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { setOnSessionEnded } from '../api/api';
import { setTokens, clearTokens, getRefreshToken, clienteCrudo } from '../api/session';

export const AuthContext = createContext();

// "Perfil incompleto" lo decide el backend (backend/utils/perfilIncompleto.js)
// y llega como user.perfil_incompleto en /users/me, PUT /users/me y el login.
// No hay copia de la regla en el front.

// ── Helpers de versión de T&C ─────────────────────────────────────────────────

// Consulta la versión vigente al backend (GET /users/terms-version)
// y compara con la que tiene el usuario. Devuelve true si necesita re-aceptar.
const checkNeedsReaccept = async (user) => {
  if (!user) return false;
  try {
    const res = await api.get('/users/terms-version');
    const currentVersion = res.data?.version;
    // Necesita re-aceptar si: nunca aceptó O su versión es anterior a la vigente
    return !user.terms_version || user.terms_version !== currentVersion;
  } catch {
    // Si el endpoint falla, no bloqueamos al usuario
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user,               setUser]               = useState(null);
  const [authReady,          setAuthReady]          = useState(false);
  const [needsTermsReaccept, setNeedsTermsReaccept] = useState(false);
  // La sesión terminó con el usuario en pantalla. NO se borra `user`: eso
  // haría que PrivateRoute desmonte la página y se pierda lo cargado. Se
  // muestra SessionExpiredModal encima (ver App.js).
  const [sessionExpired,     setSessionExpired]     = useState(false);
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // identifier puede ser email o username
  const login = async (identifier, password) => {
    const res = await api.post('/auth/login', { identifier, password });
    const { token, refresh_token, user } = res.data;

    if (!token || !user) throw new Error('Login inválido: token o user faltantes');

    // Re-login desde el modal con OTRA cuenta: no mezclar sus datos con la
    // pantalla del usuario anterior. Recarga completa, ya logueado.
    const anterior = userRef.current;
    if (anterior && anterior.id !== user.id) {
      setTokens(token, refresh_token);
      window.location.assign('/');
      return;
    }

    setTokens(token, refresh_token);

    // Obtener datos completos del usuario (incluye terms_version)
    const meRes = await api.get('/users/me');
    const fullUser = meRes.data.user || meRes.data;
    setUser(fullUser);
    setSessionExpired(false);

    // Verificar si necesita re-aceptar T&C
    const needs = await checkNeedsReaccept(fullUser);
    setNeedsTermsReaccept(needs);
  };

  const logout = useCallback(() => {
    // Revocar el refresh en el backend. Sin esperar: el logout local no puede
    // depender de que la red ande.
    const refresh = getRefreshToken();
    if (refresh) clienteCrudo.post('/auth/logout', { refresh_token: refresh }).catch(() => {});
    clearTokens();
    setUser(null);
    setNeedsTermsReaccept(false);
    setSessionExpired(false);
  }, []);

  // El interceptor avisa cuando la sesión terminó de verdad (ver api.js).
  useEffect(() => {
    setOnSessionEnded(() => {
      if (userRef.current) setSessionExpired(true);
    });
    return () => setOnSessionEnded(null);
  }, []);

  // Llamado desde el modal — el usuario acepta la versión vigente
  const acceptTerms = useCallback(async () => {
    try {
      const res = await api.post('/users/me/accept-terms');
      // Actualizar el user en contexto con los nuevos campos
      setUser(prev => ({
        ...prev,
        terms_accepted_at: res.data.terms_accepted_at,
        terms_version:     res.data.terms_version,
      }));
      setNeedsTermsReaccept(false);
    } catch (err) {
      console.error('Error aceptando términos:', err);
      throw err; // El modal muestra el error
    }
  }, []);

  // Recuperar sesión al recargar la página. Con el access vencido el
  // interceptor refresca solo; antes cualquier error acá deslogueaba, y con
  // un TTL de 10 min eso era cada recarga.
  useEffect(() => {
    if (!localStorage.getItem('token')) { setAuthReady(true); return; }

    api.get('/users/me')
      .then(async res => {
        const fullUser = res.data.user || res.data;
        setUser(fullUser);
        // También verificar T&C al restaurar sesión
        const needs = await checkNeedsReaccept(fullUser);
        setNeedsTermsReaccept(needs);
      })
      .catch(() => {
        // Si la sesión terminó, el interceptor ya borró los tokens. Si fue la
        // red o un 5xx se conservan: la próxima recarga puede recuperarla.
        setUser(null);
      })
      .finally(() => {
        setAuthReady(true);
      });
  }, []);

  return (
    <AuthContext.Provider value={{
      user, setUser,
      login, logout,
      authReady,
      needsTermsReaccept,
      acceptTerms,
      sessionExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
